import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, input, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { PaginationStore } from '../../../services/stores/pagination.store';
import { TabsStore } from '../../../services/stores/tabs.store';

@Component({
  selector: 'dr-ops-stream-ui-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit {
  readonly store = inject(PaginationStore);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly SIZER_OPTIONS = [10, 20, 30, 40, 50] as const;
  readonly totalElementsLabelSignal = computed(() => `Total ${this.store.totalItemsSignal()}`);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tabsStore = inject(TabsStore);
  readonly totalItemsSignal = input.required<number>({ alias: 'totalItems' });

  readonly entriesSizerSignal = computed(() =>
    this.SIZER_OPTIONS.map((size) => ({
      labelContent: `${size} items`,
      showLeftIcon: false,
      hasBorderAbove: true,
      hasBorderBelow: true,
      showRightIcon: false,
      isSelected: size === this.store.pageSizeSignal(),
      onListElementClick: () => this.onPageSizeSelected(size),
    }))
  );

  readonly paginationElementsSignal = computed(() =>
    Array.from({ length: this.store.totalPagesSignal() }, (_, i) => ({
      label: String(i + 1),
      anchorProperties: { href: `${this.tabsStore.getRoute()}#page-${i}` },
    }))
  );

  constructor() {
    effect(() => {
      this.store.totalItemsSignal.set(this.totalItemsSignal());
    });
  }
  ngOnInit(): void {
    this.initSyncUrlToPageIndex();
    this.initPageIndexFromUrlHash();
  }

  private parseHash(hash: string): number | null {
    const m = hash?.match(/^#?page-(\d+)$/);
    const n = m ? Number(m[1]) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  private initPageIndexFromUrlHash(): void {
    const initialFromHash = this.parseHash(location.hash);
    if (initialFromHash == null) return;
    if (initialFromHash < this.store.totalPagesSignal()) {
      this.store.pageIndexSignal.set(initialFromHash);
    } else {
      this.store.resetPageIndex();
    }
  }

  private initSyncUrlToPageIndex(): void {
    this.activatedRoute.fragment
      .pipe(
        map((f) => this.parseHash(f ?? '')),
        filter((n): n is number => n != null),
        distinctUntilChanged(),
        filter((n) => n >= 0 && n < this.store.totalPagesSignal()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((n) => this.store.pageIndexSignal.set(n));
  }

  private resetPageIndexUrlHash(): void {
    this.router.navigate([], { fragment: `page-${this.store.pageIndexSignal()}`, replaceUrl: true });
  }

  private onPageSizeSelected(size: number): void {
    this.store.pageSizeSignal.set(size);
    this.store.resetPageIndex();
    this.resetPageIndexUrlHash();
  }
}
